import React, { useState, useEffect } from 'react';
import { 
    Download, 
    Upload, 
    FileText, 
    BarChart3, 
    AlertCircle, 
    CheckCircle, 
    Brain,
    Eye,
    Trash2
} from 'lucide-react';

const AIAnalysisManager = ({ organization, user }) => {
    const [analysisData, setAnalysisData] = useState(null);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    // Load analysis data on component mount
    useEffect(() => {
        if (organization?.id) {
            loadAnalysisData();
            loadSummary();
        }
    }, [organization?.id]);

    // Load existing AI analysis data
    const loadAnalysisData = async () => {
        try {
            setLoading(true);
            const response = await fetch(`/api/ai-analysis/analysis/${organization.id}`);
            if (response.ok) {
                const data = await response.json();
                setAnalysisData(data);
            }
        } catch (error) {
            console.error('Error loading analysis data:', error);
            setError('Failed to load analysis data');
        } finally {
            setLoading(false);
        }
    };

    // Load analysis summary
    const loadSummary = async () => {
        try {
            const response = await fetch(`/api/ai-analysis/summary/${organization.id}`);
            if (response.ok) {
                const data = await response.json();
                setSummary(data);
            }
        } catch (error) {
            console.error('Error loading summary:', error);
        }
    };

    // Export features for AI analysis
    const handleExport = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await fetch('/api/ai-analysis/export-for-ai');
            if (!response.ok) {
                throw new Error('Export failed');
            }

            // Trigger download
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = 'feature-requirements-for-ai-analysis.md';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            setSuccessMessage('Feature requirements exported successfully! Share the downloaded file with your AI for analysis.');
        } catch (error) {
            console.error('Export error:', error);
            setError('Failed to export features for AI analysis');
        } finally {
            setLoading(false);
        }
    };

    // Import AI analysis results
    const handleImport = async (file) => {
        try {
            setLoading(true);
            setError(null);
            setUploadProgress('Uploading file...');

            const formData = new FormData();
            formData.append('analysisFile', file);

            const response = await fetch('/api/ai-analysis/import-analysis', {
                method: 'POST',
                body: formData
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.details || 'Import failed');
            }

            const result = await response.json();
            setUploadProgress(null);
            setSuccessMessage(`Analysis imported successfully! ${result.results.inserted} new analyses, ${result.results.updated} updated.`);
            
            // Reload data
            await loadAnalysisData();
            await loadSummary();
            
        } catch (error) {
            console.error('Import error:', error);
            setError(`Failed to import AI analysis: ${error.message}`);
            setUploadProgress(null);
        } finally {
            setLoading(false);
        }
    };

    // Generate and download analysis report
    const handleGenerateReport = async () => {
        try {
            setLoading(true);
            const response = await fetch(`/api/ai-analysis/report/${organization.id}`);
            if (!response.ok) {
                throw new Error('Report generation failed');
            }

            // Trigger download
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = 'feature-analysis-report.md';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            setSuccessMessage('Analysis report downloaded successfully!');
        } catch (error) {
            console.error('Report generation error:', error);
            setError('Failed to generate analysis report');
        } finally {
            setLoading(false);
        }
    };

    // File input change handler
    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            handleImport(file);
        }
        // Reset the input
        event.target.value = '';
    };

    // Clear messages
    const clearMessages = () => {
        setError(null);
        setSuccessMessage(null);
    };

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
                <Brain className="w-6 h-6 text-blue-600" />
                <h2 className="text-xl font-semibold text-gray-900">AI Feature Analysis</h2>
            </div>

            {/* Status Messages */}
            {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <p className="text-red-800">{error}</p>
                        <button 
                            onClick={clearMessages}
                            className="mt-2 text-sm text-red-600 hover:text-red-800"
                        >
                            Dismiss
                        </button>
                    </div>
                </div>
            )}

            {successMessage && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <p className="text-green-800">{successMessage}</p>
                        <button 
                            onClick={clearMessages}
                            className="mt-2 text-sm text-green-600 hover:text-green-800"
                        >
                            Dismiss
                        </button>
                    </div>
                </div>
            )}

            {uploadProgress && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-blue-800">{uploadProgress}</p>
                </div>
            )}

            {/* Summary Statistics */}
            {summary && (
                <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center gap-2">
                            <FileText className="w-5 h-5 text-gray-600" />
                            <span className="text-sm font-medium text-gray-600">Total Features</span>
                        </div>
                        <p className="text-2xl font-bold text-gray-900">{summary.overall.total_features}</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4">
                        <div className="flex items-center gap-2">
                            <Brain className="w-5 h-5 text-blue-600" />
                            <span className="text-sm font-medium text-blue-600">Analyzed</span>
                        </div>
                        <p className="text-2xl font-bold text-blue-900">{summary.overall.analyzed_features}</p>
                        <p className="text-sm text-blue-600">{summary.overall.coverage_percentage}% coverage</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                        <div className="flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                            <span className="text-sm font-medium text-green-600">Recommended</span>
                        </div>
                        <p className="text-2xl font-bold text-green-900">
                            {(summary.overall.strongly_recommended || 0) + (summary.overall.recommended || 0)}
                        </p>
                    </div>
                </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Export for AI */}
                    <div className="border border-gray-200 rounded-lg p-4">
                        <h3 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <Download className="w-4 h-4" />
                            Export for AI Analysis
                        </h3>
                        <p className="text-sm text-gray-600 mb-3">
                            Download a comprehensive markdown file containing all features for AI analysis.
                        </p>
                        <button
                            onClick={handleExport}
                            disabled={loading}
                            className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            <Download className="w-4 h-4" />
                            Export Features
                        </button>
                    </div>

                    {/* Import AI Analysis */}
                    <div className="border border-gray-200 rounded-lg p-4">
                        <h3 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <Upload className="w-4 h-4" />
                            Import AI Analysis
                        </h3>
                        <p className="text-sm text-gray-600 mb-3">
                            Upload the AI analysis results (JSON or Markdown with JSON).
                        </p>
                        <label className="w-full bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 cursor-pointer">
                            <Upload className="w-4 h-4" />
                            Import Analysis
                            <input
                                type="file"
                                accept=".json,.md"
                                onChange={handleFileSelect}
                                disabled={loading}
                                className="hidden"
                            />
                        </label>
                    </div>
                </div>

                {/* Generate Report */}
                {summary?.overall?.analyzed_features > 0 && (
                    <div className="border border-gray-200 rounded-lg p-4">
                        <h3 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <BarChart3 className="w-4 h-4" />
                            Generate Analysis Report
                        </h3>
                        <p className="text-sm text-gray-600 mb-3">
                            Download a comprehensive report with all AI analysis results and recommendations.
                        </p>
                        <button
                            onClick={handleGenerateReport}
                            disabled={loading}
                            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            <BarChart3 className="w-4 h-4" />
                            Generate Report
                        </button>
                    </div>
                )}
            </div>

            {/* Analysis Results Preview */}
            {analysisData && analysisData.summary && (
                <div className="mt-6 border-t border-gray-200 pt-6">
                    <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                        <Eye className="w-4 h-4" />
                        Analysis Overview
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        {Object.entries(analysisData.summary.recommendations || {}).map(([recommendation, count]) => (
                            <div key={recommendation} className="text-center">
                                <p className="font-medium text-gray-900">{count}</p>
                                <p className="text-gray-600">{recommendation}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Instructions */}
            <div className="mt-6 bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">How to Use AI Analysis</h4>
                <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
                    <li>Click "Export Features" to download a markdown file with all features</li>
                    <li>Share this file with an AI assistant (ChatGPT, Claude, etc.) for analysis</li>
                    <li>Ask the AI to provide recommendations following the specified format</li>
                    <li>Save the AI's response as a JSON or markdown file</li>
                    <li>Use "Import Analysis" to upload the AI's recommendations</li>
                    <li>Generate reports to view comprehensive analysis results</li>
                </ol>
            </div>
        </div>
    );
};

export default AIAnalysisManager; 